import type { Tenant } from '@peruselab/api-client/tenants';

/**
 * #1051 -- remembering which tenant a user last acted for, on this device. `null` is their
 * personal tenant. Browser storage can be unavailable (private windows, blocked site data), so
 * every access falls back to the personal tenant.
 */
const storageKey = (userId: string) => `peruselab.activeTenant.${encodeURIComponent(userId)}`;

export function rememberedTenant(userId: string): string | null {
  try {
    return localStorage.getItem(storageKey(userId)) || null;
  } catch {
    return null;
  }
}

export function rememberTenant(userId: string, tenantId: string | null): void {
  try {
    if (tenantId) localStorage.setItem(storageKey(userId), tenantId);
    else localStorage.removeItem(storageKey(userId));
  } catch {
    /* Not remembered: the next visit starts in the personal tenant. */
  }
}

/** The remembered tenant if the user still belongs to it, else the personal tenant. */
export function resolveActiveTenant(tenants: readonly Tenant[], remembered: string | null): Tenant | null {
  return (remembered && tenants.find((tenant) => tenant.id === remembered)) || null;
}

/** A key that changes whenever what the user sees belongs to someone else: user and tenant. */
export function tenantScopeKey(userId: string | null, tenantId: string | null): string {
  return `${userId ?? 'anonymous'}:${tenantId ?? 'personal'}`;
}
